import { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import { PanelRightOpen } from 'lucide-react'
import BaseLabel from '../../../components/general/BaseLabel'
import { TSoftwareFunctions } from '../../../types/index'

gsap.registerPlugin(ScrollTrigger)

type SoftwareFeatureScrollMobileProps = Pick<
    TSoftwareFunctions,
    | 'step1Label' | 'step1Headline'
    | 'step2Label' | 'step2Headline'
    | 'logoText' | 'labelSidebar' | 'bottomTextSidebar'
    | 'coreFunctions' | 'extensions'
>

export default function SoftwareFeatureScrollMobile({
    step1Label,
    step1Headline,
    step2Label,
    step2Headline,
    logoText,
    labelSidebar,
    bottomTextSidebar,
    coreFunctions,
    extensions,
}: SoftwareFeatureScrollMobileProps) {
    const containerRef = useRef<HTMLElement>(null)
    const title1Ref = useRef<HTMLDivElement>(null)
    const title2Ref = useRef<HTMLDivElement>(null)
    const coreViewportRef = useRef<HTMLDivElement>(null)
    const coreTrackRef = useRef<HTMLDivElement>(null)
    const extViewportRef = useRef<HTMLDivElement>(null)
    const extTrackRef = useRef<HTMLDivElement>(null)
    const panelButtonRef = useRef<HTMLButtonElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const extTextRef = useRef<HTMLParagraphElement>(null)
    const coreRefs = useRef<(HTMLDivElement | null)[]>([])
    const extRefs = useRef<(HTMLDivElement | null)[]>([])

    const coreCardMinHeight = 88
    const coreGap = 12
    const coreTailSpace = 96
    const extCardMinHeight = 74
    const extGap = 16
    const [coreScrollDistance, setCoreScrollDistance] = useState(0)
    const [extScrollDistance, setExtScrollDistance] = useState(0)

    const measureCoreScrollDistance = () => {
        const measuredHeights = coreFunctions.map((_, i) => {
            const h = coreRefs.current[i]?.getBoundingClientRect().height ?? 0
            return h > 0 ? h : coreCardMinHeight
        })

        const totalHeight = measuredHeights.reduce((sum, h) => sum + h, 0)
            + Math.max(0, measuredHeights.length - 1) * coreGap
            + coreTailSpace
        const viewportHeight = coreViewportRef.current?.getBoundingClientRect().height ?? 0

        return Math.max(0, totalHeight - viewportHeight)
    }

    const measureExtensionScrollDistance = () => {
        const measuredHeights = extensions.map((_, i) => {
            const h = extRefs.current[i]?.getBoundingClientRect().height ?? 0
            return h > 0 ? h : extCardMinHeight
        })

        const totalHeight = measuredHeights.reduce((sum, h) => sum + h, 0) + Math.max(0, measuredHeights.length - 1) * extGap
        const viewportHeight = extViewportRef.current?.getBoundingClientRect().height ?? 0

        return Math.max(0, totalHeight - viewportHeight)
    }

    useEffect(() => {
        const updateMeasurements = () => {
            setCoreScrollDistance(measureCoreScrollDistance())
            setExtScrollDistance(measureExtensionScrollDistance())
        }

        updateMeasurements()
        window.addEventListener('resize', updateMeasurements)

        return () => {
            window.removeEventListener('resize', updateMeasurements)
        }
    }, [coreFunctions.length, extensions.length])

    useGSAP(() => {
        gsap.set(panelRef.current, { xPercent: 112, autoAlpha: 0 })
        gsap.set(extTextRef.current, { autoAlpha: 0 })

        extRefs.current.forEach((el) => {
            if (!el) return
            gsap.set(el, { autoAlpha: 0, y: 8 })
        })

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: containerRef.current,
                start: 'top top',
                end: 'bottom bottom',
                scrub: 1.2,
            },
        })

        // Phase 1: core list scroll in app viewport.
        if (coreScrollDistance > 0) {
            tl.to(coreTrackRef.current, { y: -coreScrollDistance, duration: 40, ease: 'none' }, 8)
        }

        // Phase 2: switch headline and simulate a panel-open click.
        tl.to(title1Ref.current, { opacity: 0, duration: 4 }, 46)
        tl.fromTo(title2Ref.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 4 }, 52)
        tl.to(panelButtonRef.current, { scale: 0.86, duration: 1.1 }, 62)
        tl.to(panelButtonRef.current, { scale: 1, duration: 1.2, ease: 'back.out(2.1)' }, 63.2)
        tl.to(panelRef.current, { xPercent: 0, autoAlpha: 1, duration: 7, ease: 'power2.out' }, 64)

        // Phase 3: reveal extension options in the opened panel.
        const extStart = 72
        const extLen = 20
        const extDur = extLen / Math.max(extensions.length, 1)

        if (extScrollDistance > 0) {
            tl.to(
                extTrackRef.current,
                { y: -extScrollDistance, duration: extLen - 4, ease: 'none' },
                extStart + 2,
            )
        }

        extRefs.current.forEach((el, i) => {
            tl.fromTo(
                el,
                { autoAlpha: 0, y: 8 },
                { autoAlpha: 1, y: 0, duration: Math.max(extDur * 0.7, 1.6) },
                extStart + i * extDur,
            )
        })

        tl.to(extTextRef.current, { autoAlpha: 1, duration: 3 }, 92)
        tl.to({}, { duration: 8 }, 94)
    }, { scope: containerRef, dependencies: [coreFunctions.length, extensions.length, coreScrollDistance, extScrollDistance] })

    return (
        <section ref={containerRef} className="relative h-[380vh] lg:hidden bg-base-100 pb-24">
            <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden px-4">
                <div className="absolute top-20 left-0 w-full text-center z-20 pointer-events-none px-4">
                    <div ref={title1Ref} className="absolute inset-0 flex flex-col items-center" style={{ opacity: 1 }}>
                        <BaseLabel label={step1Label} color="primary" classProps='!text-xs !mb-0 !pb-0'/>
                        <h2 className="font-headline font-bold text-2xl text-base-content mt-2  leading-tight">
                            {step1Headline}
                        </h2>
                    </div>
                    <div ref={title2Ref} className="absolute inset-0 flex flex-col items-center" style={{ opacity: 0 }}>
                        <BaseLabel label={step2Label} color="secondary" classProps='!text-xs !mb-0 !pb-0' />
                        <h2 className="font-headline font-bold text-2xl text-base-content mt-2 leading-tight">
                            {step2Headline}
                        </h2>
                    </div>
                </div>

                <div className="w-full max-w-[390px] h-[74vh] max-h-[720px] bg-base-100 border border-base-300/60 rounded-[2rem] shadow-2xl overflow-hidden relative mt-32">
                    <div className="h-8 bg-base-200 border-b border-base-300/60 px-4 flex items-center justify-center">
                        <div className="h-1.5 w-20 rounded-full bg-base-content/20" />
                    </div>

                    <div className="h-[calc(100%-2rem)] relative bg-base-100 overflow-hidden">
                        <div className="absolute inset-0 p-4 flex flex-col bg-base-100">
                            <div className="flex items-center gap-2.5 mb-4">
                                <div className="w-6 h-6 rounded bg-primary/20 flex items-center justify-center shrink-0">
                                    <div className="w-2.5 h-2.5 bg-primary rounded-sm" />
                                </div>
                                <span className="font-headline font-bold text-base-content/70 text-sm truncate">
                                    {logoText}
                                </span>
                            </div>

                            <div className="space-y-2 mb-4 shrink-0">
                                <div className="h-7 bg-base-content/10 rounded w-full" />
                                <div className="h-7 bg-base-content/5 rounded w-5/6" />
                            </div>

                            <div ref={coreViewportRef} className="flex-1 overflow-hidden min-h-0 rounded-xl border border-base-300/60 bg-base-200/40 p-2.5">
                                <div ref={coreTrackRef} className="flex flex-col gap-3 will-change-transform">
                                    {coreFunctions.map((core, i) => (
                                        <div
                                            key={i}
                                            ref={(el) => { coreRefs.current[i] = el }}
                                            className="bg-base-100 border border-base-300/60 rounded-lg p-3 min-h-[88px]"
                                        >
                                            <p className="text-[14px] font-bold uppercase tracking-wider text-base-content/80">
                                                {core.title}
                                            </p>
                                            <p className="text-[12px] leading-snug text-base-content/60 mt-1.5">
                                                {core.description}
                                            </p>
                                            <div className="h-8 w-full bg-base-content/5 rounded-md mt-2" />
                                        </div>
                                    ))}
                                    <div className="h-24 shrink-0" aria-hidden="true" />
                                </div>
                            </div>
                        </div>

                        <button
                            ref={panelButtonRef}
                            type="button"
                            aria-label="Open extensions panel"
                            className="absolute right-3 top-3 z-20 h-10 w-10 rounded-xl border border-secondary/40 bg-secondary/15 text-secondary shadow-lg flex items-center justify-center"
                        >
                            <PanelRightOpen size={18} strokeWidth={2.25} />
                        </button>

                        <div
                            ref={panelRef}
                            className="absolute top-0 right-0 h-full w-[80%] bg-base-200 border-l border-base-300/70 px-4 pt-6 z-30"
                        >
                            <p className="text-[14px] font-bold text-secondary uppercase tracking-widest mb-6">
                                {labelSidebar}
                            </p>
                            <div ref={extViewportRef} className="overflow-hidden max-h-[85%] min-h-0 pr-1">
                                <div ref={extTrackRef} className="space-y-4 will-change-transform">
                                    {extensions.map((ext, i) => (
                                        <div
                                            key={i}
                                            ref={(el) => { extRefs.current[i] = el }}
                                            style={{ opacity: 0, visibility: 'hidden' }}
                                            className="bg-secondary/10 border border-secondary/30 rounded-md px-2.5 py-2"
                                        >
                                            <p className="text-secondary text-[14px] font-bold leading-tight">
                                                {ext.title}
                                            </p>
                                            <p className="text-base-content/50 text-[13px] leading-tight mt-1">
                                                {ext.description}
                                            </p>
                                        </div>
                                    ))}
                                    {extensions.length === 0 && (
                                        <p className="text-[10px] text-base-content/50 italic">Keine Erweiterungen hinterlegt.</p>
                                    )}
                                </div>
                            </div>

                            <p
                                ref={extTextRef}
                                className="text-base-content/80 text-[14px] italic leading-relaxed mt-4"
                                style={{ opacity: 0 }}
                            >
                                {bottomTextSidebar}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}